import { useEffect, useRef, useState, useCallback } from 'react';
import { API } from '../../lib/api';

/**
 * Custom hook to subscribe to overlay SSE events.
 * @param {string} token - Overlay token
 * @param {function} onEvent - Callback for each event
 */
export default function useOverlaySSE(token, onEvent) {
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    document.documentElement.classList.add('overlay-body');
    document.body.classList.add('overlay-body');
    // Also try to set background directly just in case
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    
    return () => {
      document.documentElement.classList.remove('overlay-body');
      document.body.classList.remove('overlay-body');
      document.body.style.background = '';
      document.documentElement.style.background = '';
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    // Use the exact API endpoint configured for the app to guarantee consistency
    const url = `${API}/overlay/events/${token}`;

    const timeoutRef = { current: null };
    const connect = () => {
      console.log(`[SSE] Connecting to ${url}...`);
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        console.log('[SSE] Connected successfully');
        setConnected(true);
      };

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type !== 'ping') {
            console.log('[SSE] Event received:', data);
            cbRef.current(data);
          }
        } catch (err) {
          console.error('[SSE] Parse error:', err);
        }
      };

      es.onerror = (err) => {
        console.error('[SSE] Connection error:', err);
        setConnected(false);
        es.close();
        // Auto-reconnect after 3 seconds
        timeoutRef.current = setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      if (esRef.current) esRef.current.close();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [token]);

  return { connected };
}
